import type { FaqHintRule, HintPattern } from './faq-hint.utils';

/**
 * Stichwort-Regeln fürs Kontaktformular (#1071).
 *
 * Bewusst fest im Code und ohne Pflegeoberfläche. Die Regeln decken nur die
 * Themen ab, bei denen der Hinweis den Eltern wirklich hilft — Modulwechsel und
 * abgelehnte IBANs stehen absichtlich NICHT hier: dort ist der Nutzer im Recht
 * und ein FAQ-Hinweis wäre eine Ausrede.
 *
 * Die Stichwörter stehen bereits normalisiert da (Kleinschreibung, Umlaute als
 * ae/oe/ue) — geprüft wird gegen eine ebenso normalisierte Nachricht, auf
 * Wortgrenzen. Kein Text hier ist übersetzt: Die Karte im Formular zeigt die
 * verlinkte FAQ-Frage, deren Übersetzung aus `faq.data.ts` kommt.
 */

/**
 * Tagesbezug. Entscheidet die Zweideutigkeit von „abmelden": mit Tagesbezug
 * geht es um einen einzelnen Tag (Betreuung vor Ort), ohne um den Vertrag.
 */
const DAY_REFERENCES: readonly HintPattern[] = [
  'heute',
  'morgen',
  'uebermorgen',
  'montag',
  'dienstag',
  'mittwoch',
  'donnerstag',
  'freitag',
  // „am 12.09.", „am 12.9." — ein blosses Datum ohne „am" bleibt aussen vor,
  // sonst würde „zum 31.07. abmelden" (Vertragsende) als Tagesbezug gelten.
  /\bam \d{1,2}\. ?\d{0,2}\.?/,
];

/**
 * Die ganze Wortfamilie rund um „kündigen": kuendigen, kuendigung, gekuendigt,
 * kuendigungsfrist, kuendigungsbestaetigung. Als Ausdruck statt als Liste, weil
 * das Matching sonst auf exakten Wortgrenzen sitzt und jede Beugung einzeln
 * gepflegt werden müsste.
 */
const KUENDIGUNG_FAMILY = /\b(?:ge)?kuendig\w*/;

/**
 * Vertragsbezug — schliesst den Tagesbezug bei „abmelden" wieder aus. Umzug
 * und Schulwechsel stehen mit drin: Das sind die Sonderkündigungsfälle aus der
 * FAQ, und „am 01.08. abmelden, wir ziehen um" ist eine Kündigung, kein
 * Fehltag.
 */
const CONTRACT_TERMS: readonly HintPattern[] = [
  /\bvertrag\w*/,
  'betreuungsvertrag',
  KUENDIGUNG_FAMILY,
  /\bschuljahr\w*/,
  'endgueltig',
  'dauerhaft',
  /\bumzug\w*/,
  /\bumziehen\w*/,
  'ziehen um',
  'ziehen wir um',
  /\bschulwechsel\w*/,
  /\bwegzug\w*/,
];

/**
 * „Abmelden" von etwas, das kein Betreuungsvertrag ist: Ferienbetreuung und
 * Mittagessen haben eigene Regeln, ein Newsletter gehört gar nicht hierher.
 */
const NON_CONTRACT_SUBJECTS: readonly HintPattern[] = [
  /\bferien\w*/,
  /\bmittagessen\w*/,
  'essen',
  'newsletter',
];

/**
 * Die getrennte Verbform: „Bitte melden Sie meine Tochter morgen … ab."
 * Das „ab" muss am Satzende stehen, damit „melde mich, sobald der Vertrag ab
 * August läuft" nicht als Abmeldung durchgeht.
 */
const SEPARATED_ABMELDEN = /\bmelde\w* [^!?]{0,60}\bab(?=[.,;!?]|$)/;

/** Alle Formen von „abmelden" — je nach Regel mit Tages- oder Vertragsbezug. */
const ABMELDEN_PATTERNS: readonly HintPattern[] = [
  'abmelden',
  'abmeldung',
  'abzumelden',
  SEPARATED_ABMELDEN,
];

/** Was man mit einer Ferienbuchung tut: stornieren, tauschen, einzelne Tage, umbuchen. */
const FERIEN_ACTIONS: readonly HintPattern[] = [
  ...ABMELDEN_PATTERNS,
  /\bstornier\w*/,
  /\bstorno\w*/,
  /\btausch\w*/,
  /\bumbuch\w*/,
  /\bbuch\w*/,
  /\baender\w*/,
  'absagen',
  'einzelne tage',
  'einzelnen tag',
  'einzelner tag',
  'nur einen tag',
  'einen tag',
];

/** Ein Änderungsantrag hat eine eigene Bearbeitungsdauer-FAQ. */
const ALTERATION_TERMS: readonly HintPattern[] = [
  /\baenderungsantr\w*/,
  /\bvertragsaenderung\w*/,
];

/** Stichwörter für den Bearbeitungsstand — einmal für die Anmeldung, einmal für den Änderungsantrag. */
const STATUS_PATTERNS: readonly HintPattern[] = [
  'in pruefung',
  'in bearbeitung',
  'keine bestaetigung',
  'keine rueckmeldung',
  'nichts gehoert',
  'wie lange dauert',
  'seit wochen',
];

/**
 * Eine abgelehnte oder fehlerhafte Bankverbindung ist ein Fall für die
 * Verwaltung (oft ein Bug) — dort darf der Hinweis „Sie können sie selbst
 * ändern" nicht erscheinen.
 */
const BANK_REJECTION_TERMS: readonly HintPattern[] = [
  /\babgelehnt\w*/,
  'nicht akzeptiert',
  'nicht angenommen',
  /\bungueltig\w*/,
  'invalid',
  /\bfehler\w*/,
  'geht nicht',
  'funktioniert nicht',
  'klappt nicht',
];

/** Stichwörter, die eine Nachricht als reine Rückruf-Bitte ausweisen. */
export const CALLBACK_PATTERNS: readonly HintPattern[] = [
  'rueckruf',
  'zurueckrufen',
  'zurueck rufen',
  'rufen sie mich an',
  'rufen sie mich zurueck',
  'rufen sie mich bitte',
  'anrufen',
  'telefonisch melden',
];

export const FAQ_HINT_RULES: readonly FaqHintRule[] = [
  {
    // Krankmeldung und Fehltage gehören in die Einrichtung vor Ort, nicht ins
    // Portal — 15 Tickets im Auswertungszeitraum.
    id: 'krankmeldung',
    faqIds: ['kind-krank'],
    priority: 90,
    patterns: [
      'krank',
      'kranke',
      'erkrankt',
      'krankgemeldet',
      'krank gemeldet',
      'krankmeldung',
      'krank melden',
      'fieber',
      'grippe',
      'magen darm',
      'fehlt heute',
      'fehlt morgen',
      'kann nicht kommen',
      'kommt heute nicht',
      'kommt morgen nicht',
      'kind kommt nicht',
    ],
  },
  {
    // Tagesgeschäft: früher abholen, allein heimgehen, einzelner Fehltag.
    id: 'tagesbetrieb',
    faqIds: ['kind-frueher-abholen'],
    priority: 100,
    patterns: [
      'allein nach hause',
      'alleine nach hause',
      'allein heim',
      'alleine heim',
      'allein heimlaufen',
      'alleine heimlaufen',
      'selbststaendig nach hause',
      'frueher abholen',
      'frueher abgeholt',
      'frueher nach hause',
      'eher abholen',
      'arzttermin',
      'zahnarzttermin',
      { pattern: 'abholen', requiresAny: DAY_REFERENCES },
      ...ABMELDEN_PATTERNS.map((pattern) => ({
        pattern,
        requiresAny: DAY_REFERENCES,
        forbidsAny: CONTRACT_TERMS,
      })),
      { pattern: 'nicht teilnehmen', requiresAny: DAY_REFERENCES },
    ],
  },
  {
    // Mittagessen — grösster Cluster (24 Tickets): „steht auf Nein", wird beim
    // Caterer gebucht, nicht im Portal.
    id: 'mittagessen',
    faqIds: ['mittagessen-buchen', 'nur-essen-buchen'],
    priority: 80,
    patterns: [
      /\bmittagessen\w*/,
      'essen',
      /\bcaterer\w*/,
      /\bcatering\w*/,
      /\bessensbestellung\w*/,
      'essensgeld',
      /\bverpflegung\w*/,
      'mittagsverpflegung',
    ],
  },
  {
    // Steuerbescheinigungen gibt es seit dem Elternbrief vom 02.03.2026 nicht
    // mehr — die FAQ erklärt, warum. Amtsbescheinigungen und Vertragskopien
    // stellt die Verwaltung weiterhin aus.
    id: 'bescheinigung',
    faqIds: ['steuerbescheinigung', 'vertrag-finden'],
    priority: 70,
    patterns: [
      'steuer',
      'steuererklaerung',
      'steuerbescheinigung',
      'finanzamt',
      'bescheinigung',
      'nachweis',
      'jobcenter',
      'betreuungskosten',
      'vertrag als pdf',
      'vertragskopie',
      'kopie des vertrags',
    ],
  },
  {
    // Neue Bankverbindung pflegen die Eltern selbst — abgelehnte IBANs bleiben
    // bewusst aussen vor (s. Kopf der Datei).
    id: 'bankverbindung',
    faqIds: ['bankverbindung-aendern'],
    priority: 65,
    patterns: [
      /\bbankverbindung\w*/,
      'iban',
      /\bkontonummer\w*/,
      /\bbankdaten\w*/,
      /\bbankwechsel\w*/,
      'neues konto',
      'anderes konto',
      'konto geaendert',
      'konto gewechselt',
      'bank gewechselt',
    ],
    excludes: BANK_REJECTION_TERMS,
  },
  {
    // Abbuchung, Lastschrift, Rückzahlung.
    id: 'abbuchung',
    faqIds: ['abbuchungstermin', 'abbuchung-trotz-kuendigung'],
    priority: 60,
    patterns: [
      'abgebucht',
      'abbuchung',
      'abbuchen',
      'lastschrift',
      'ruecklastschrift',
      'eingezogen',
      'einzug',
      'rueckzahlung',
      'zurueckgebucht',
      'beitrag',
      'beitraege',
    ],
  },
  {
    // Ferienbetreuung — drittgrösster Cluster (20 Tickets): Stornieren, Woche
    // tauschen, einzelne Tage. „Von der Ferienbetreuung abmelden" ist keine
    // Vertragskündigung.
    id: 'ferienbetreuung',
    faqIds: ['ferienbetreuung-stornieren', 'ferienbetreuung-einzelne-tage'],
    priority: 55,
    // Nur, wenn es um eine Buchung geht — eine Abbuchung „für die
    // Ferienbetreuung" bleibt beim Abbuchungs-Hinweis.
    patterns: [{ pattern: /\bferien\w*/, requiresAny: FERIEN_ACTIONS }],
  },
  {
    // „Seit Wochen in Prüfung" — zweitgrösster Cluster.
    id: 'bearbeitungsstatus',
    faqIds: ['anmeldung-bearbeitungsdauer'],
    priority: 50,
    patterns: [...STATUS_PATTERNS, 'status der anmeldung'],
    excludes: ALTERATION_TERMS,
  },
  {
    // Derselbe Wortlaut, aber es geht um einen Änderungsantrag — der hat eine
    // eigene FAQ zur Bearbeitungsdauer.
    id: 'bearbeitungsstatus-antrag',
    faqIds: ['aenderungsantrag-bearbeitungsdauer'],
    priority: 50,
    patterns: STATUS_PATTERNS.map((pattern) => ({
      pattern,
      requiresAny: ALTERATION_TERMS,
    })),
  },
  {
    // Name und Geburtsdatum des Kindes stehen im Vertrag und werden von der
    // Verwaltung korrigiert — der Hinweis sagt, welche Angaben in die
    // Nachricht gehören.
    id: 'stammdaten',
    faqIds: ['stammdaten-korrigieren'],
    priority: 45,
    patterns: [
      'geburtsdatum falsch',
      'geburtsdatum ist falsch',
      'geburtsdatum stimmt nicht',
      'geburtsdatum aendern',
      'falsches geburtsdatum',
      'name falsch',
      'name ist falsch',
      'name stimmt nicht',
      'falscher name',
      'falsch geschrieben',
      'falsch eingetragen',
      /\bschreibfehler\w*/,
      /\btippfehler\w*/,
      'vertippt',
      'nachname aendern',
      'vorname aendern',
      /\bnamensaenderung\w*/,
    ],
  },
  {
    // Notfallkontakt und „mitversichert bei" — kein Selfservice, die
    // Verwaltung trägt nach.
    id: 'notfallkontakt',
    faqIds: ['notfallkontakt-aendern'],
    priority: 45,
    patterns: [
      /\bnotfallkontakt\w*/,
      /\bnotfallnummer\w*/,
      'mitversichert',
      /\babholberechtigt\w*/,
    ],
  },
  {
    // Kündigung — die FAQ dazu gibt es längst, sie wird nur nicht gefunden.
    id: 'kuendigung',
    faqIds: ['vertrag-kuendigen'],
    priority: 40,
    patterns: [
      KUENDIGUNG_FAMILY,
      'vertrag beenden',
      'betreuung beenden',
      'vertrag aufloesen',
      'ruecktritt',
      'zuruecktreten',
      // „abmelden" ohne Tagesbezug ist eine Kündigung …
      ...ABMELDEN_PATTERNS.map((pattern) => ({
        pattern,
        forbidsAny: [...DAY_REFERENCES, ...NON_CONTRACT_SUBJECTS],
      })),
      // … und mit Vertragsbezug auch dann, wenn nebenbei ein Tag genannt wird
      // („zum Schuljahresende abmelden. Heute noch eine Frage …",
      // „am 01.08. abmelden, wir ziehen um").
      ...ABMELDEN_PATTERNS.map((pattern) => ({
        pattern,
        requiresAny: CONTRACT_TERMS,
        forbidsAny: NON_CONTRACT_SUBJECTS,
      })),
    ],
  },
];
